import { useActivityAudio } from './audio-context';

/**
 * Floating speaker-icon button — toggles the Activity's audio cues on/off
 * (#3972). Positioned top-right of the iframe via `.activity-audio-toggle`
 * so it lives outside any view's flow and is reachable from every authorized
 * surface (`<AuthorizedView>` mounts this once for the consolidated view).
 *
 * Accessibility:
 *
 *  - Real `<button>` with `aria-pressed` reflecting the muted state.
 *  - `aria-label` carries the current state in plain text for screen readers
 *    (the icon alone is not a meaningful label).
 *  - Keyboard reachable via the standard tab order; `outline` styling stays
 *    on the default focus ring (see audio.css).
 *
 * Persistence is owned by `<ActivityAudioProvider>` — toggling here writes
 * through to localStorage keyed on the Discord user id.
 */
export function AudioMuteToggle() {
  const { muted, setMuted } = useActivityAudio();
  const label = muted ? 'Unmute Activity sounds' : 'Mute Activity sounds';
  return (
    <button
      type="button"
      className="activity-audio-toggle"
      aria-pressed={muted}
      aria-label={label}
      title={label}
      onClick={() => setMuted(!muted)}
    >
      <SpeakerIcon muted={muted} />
    </button>
  );
}

function SpeakerIcon({ muted }: { readonly muted: boolean }) {
  // Two distinct paths so the icon swap is unambiguous at a glance — the
  // muted variant adds a slash to the same speaker silhouette.
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 5L6 9H2v6h4l5 4V5z" />
      {muted ? (
        <>
          <line x1="22" y1="9" x2="16" y2="15" />
          <line x1="16" y1="9" x2="22" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.54 8.46a5 5 0 010 7.07" />
          <path d="M19.07 4.93a10 10 0 010 14.14" />
        </>
      )}
    </svg>
  );
}
